import type { GanttAgendaTask, GanttAssignee, GanttGroup, GanttTask } from "../../GanttChart";
import { KANBAN_COLUMN_LABELS, KANBAN_COLUMN_ORDER, OBJECT_TYPE_LABELS } from "../constants";
import { KanbanTask, TasksDashboardGroupRow } from "../models";
import { resolveTaskColumn } from "./utils";

/*
  Gantt status = the board's *resolved Kanban column*, so the two views label the same task the same
  way. The design's six statuses map 1:1 to the six columns, and the board derives a task's column via
  `resolveTaskColumn` (Completed if it is complete, else Unassigned if it has no assignee, else its
  status). So an incomplete task with no assignee reads as "Unassigned" regardless of its underlying
  status — intentional, and exactly the Kanban's Unassigned column (status is treated as the column,
  not as an axis orthogonal to assignment).
*/

/**
 * Parse the board's date strings deterministically. Two formats are supported:
 *  - ISO `yyyy-mm-dd` (an optional time suffix is ignored)
 *  - the board's display format `dd/mm/yyyy` (UK / en-GB — **day first**)
 * Slashed dates are always day-first; we never fall back to the JS engine's locale parsing, which
 * would ambiguously read e.g. `05/03/2026` as mm/dd. Anything else returns undefined (the task
 * simply gets no marker) rather than a silently wrong date.
 */
const parseDate = (value?: string): Date | undefined => {
  if (!value) return undefined;
  const iso = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
  const dmy = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (dmy) return new Date(Number(dmy[3]), Number(dmy[2]) - 1, Number(dmy[1]));
  return undefined;
};

const toAssignees = (task: KanbanTask): GanttAssignee[] | undefined => {
  const initials = task.assigneeInitials ?? task.assigneeName?.slice(0, 2).toUpperCase();
  if (!initials) return undefined;
  // The uuid is the assignee's real identity where the host supplied one — two people can share a
  // name, so falling back to the name only holds while nothing keys off the id.
  return [{ uuid: task.assigneeUuid ?? task.assigneeName ?? initials, initials, name: task.assigneeName }];
};

const toGanttTask = (task: KanbanTask): GanttTask => ({
  id: task.id,
  name: task.title,
  // The deadline is `dueDate`; `endDate` is only a defensive fallback.
  deadlineDate: parseDate(task.dueDate) ?? parseDate(task.endDate),
  status: KANBAN_COLUMN_LABELS[resolveTaskColumn(task)],
  isMilestone: Boolean(task.isMilestone),
  stageName: task.stage,
  phase: task.phase,
  assignees: toAssignees(task),
});

/**
 * The server's lane counts as the chart's status totals, in the board's column order and with the
 * empty lanes dropped. Labelled from the same map the rows use, so a count and the rows under it
 * never read as different statuses.
 */
const toStatusCounts = (
  countsByColumnId: TasksDashboardGroupRow["countsByColumnId"]
): { status: string; count: number }[] | undefined => {
  if (!countsByColumnId) return undefined;

  return KANBAN_COLUMN_ORDER.filter((columnId) => (countsByColumnId[columnId] ?? 0) > 0).map((columnId) => ({
    status: KANBAN_COLUMN_LABELS[columnId],
    count: countsByColumnId[columnId] as number,
  }));
};

/**
 * Map the flat, already date-sorted agenda rows into the chart's agenda shape. Each row carries its own
 * parent object, since the agenda mixes objects rather than sitting under a group header. The type is
 * always labelled here: a flat list gives no other clue which object a row belongs to.
 */
export const toGanttAgendaTasks = (tasks: KanbanTask[]): GanttAgendaTask[] =>
  tasks.map((task) => ({
    ...toGanttTask(task),
    objectId: task.linkedObjectUuid ?? "",
    objectName: task.linkedObjectName,
    objectType: task.linkedObjectType
      ? (OBJECT_TYPE_LABELS[task.linkedObjectType] ?? task.linkedObjectType)
      : undefined,
  }));

interface ToGanttGroupsOptions {
  /**
   * Whether to surface the project/deal type badge on each group. Intended to be true only when
   * the group set spans more than one object type — projects-only users then see no (redundant)
   * label. Defaults to false.
   */
  showObjectType?: boolean;
}

/**
 * Map server-supplied group rows into GanttChart groups, keeping the server's counts and ordering.
 * The rows arrive per group and are empty until the group is expanded, so the header counts come
 * from the server rather than from the loaded rows.
 */
export const serverGroupsToGanttGroups = (
  groups: TasksDashboardGroupRow[],
  options: ToGanttGroupsOptions = {}
): GanttGroup[] =>
  groups.map((group) => ({
    // The object uuid is the group id, so expand/click callbacks identify the object itself.
    id: group.objectUuid,
    name: group.objectName,
    objectType:
      options.showObjectType && group.objectType
        ? (OBJECT_TYPE_LABELS[group.objectType.toLowerCase()] ?? group.objectType)
        : undefined,
    tasks: (group.tasks ?? []).map(toGanttTask),
    taskCount: group.taskCount,
    milestoneTaskCount: group.milestoneTaskCount,
    overdueTaskCount: group.overdueTaskCount,
    statusCounts: toStatusCounts(group.countsByColumnId),
    pagedTaskCount: group.pagedTaskCount,
    isLoadingTasks: group.isLoadingTasks,
    hasMoreTasks: group.hasMoreTasks,
  }));
